import { useState } from "react";

export interface OverviewTemplateStepInfo {
    id: string;
    name: string;
    order: number;
}

// The overview tab and the all information tab are fixed and come before the template's own steps.
export enum OverviewTopTabIndex {
    Main = 0,
    AllInfo = 1,
}

// The two fixed tabs occupy index 0 and 1, so a step's own order starts after them.
export function toTopTabIndex(stepOrder: number) {
    return stepOrder + 2;
}

const mainTabId = "main";

export function useOverviewTopTab(
    storageKeyPrefix: string,
    allInfoTabId: string,
    entityTemplateId: string | undefined,
    templateSteps: Array<OverviewTemplateStepInfo> | undefined
) {
    // Keyed by template rather than by entity, so moving between entities on the same template keeps the user
    // on the step they were reading. The step's id is stored, so renaming or reordering steps still lands on
    // the same step.
    const storageKey = entityTemplateId ? `${storageKeyPrefix}-${entityTemplateId}` : undefined;

    // Read through to storage instead of mirroring it into state, so the stored tab is already selected on the
    // first render the template is known rather than flashing the overview tab and then correcting. The pick is
    // kept against the key it was made for, so a different template falls back to what that template stored.
    const [pickedTab, setPickedTab] = useState<{ storageKey: string; tabId: string }>();
    const storedTabId = !storageKey
        ? mainTabId
        : ((pickedTab?.storageKey === storageKey ? pickedTab.tabId : undefined) ?? localStorage.getItem(storageKey) ?? mainTabId);

    const selectedTopTab = resolveTopTabIndexFromStoredId(storedTabId, allInfoTabId, templateSteps);

    const setSelectedTopTab = (index: number) => {
        const tabId = resolveTopTabIdFromIndex(index, allInfoTabId, templateSteps);
        if (storageKey) {
            localStorage.setItem(storageKey, tabId);
            setPickedTab({ storageKey, tabId });
        }
    };

    return { selectedTopTab, setSelectedTopTab };
}

function resolveTopTabIndexFromStoredId(storedId: string, allInfoTabId: string, steps: Array<OverviewTemplateStepInfo> | undefined) {
    if (storedId === allInfoTabId) {
        return OverviewTopTabIndex.AllInfo;
    }

    const matchingStep = steps?.find((step) => step.id === storedId);
    return matchingStep ? toTopTabIndex(matchingStep.order) : OverviewTopTabIndex.Main;
}

function resolveTopTabIdFromIndex(index: number, allInfoTabId: string, steps: Array<OverviewTemplateStepInfo> | undefined) {
    if (index === OverviewTopTabIndex.Main) {
        return mainTabId;
    }

    if (index === OverviewTopTabIndex.AllInfo) {
        return allInfoTabId;
    }

    return steps?.find((step) => toTopTabIndex(step.order) === index)?.id ?? mainTabId;
}
